import React from "react";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import axios from "axios";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";

import { SimulatedQuotasTable } from "src/adminPanel/modelQuotas/SimulatedQuotasTable";
import { usePatchWorkspace } from "src/adminPanel/queries";
import { SimulateQuotasRequest } from "src/aiNode/types";
import { useSimulatedQuotas } from "src/api/connectApi/queries";
import { useCreateOperation } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import {
  FlowApiSchemasModelUsageFeatureOverride,
  FlowApiSchemasModelUsageTier,
  WorkspaceConfig,
  WorkspaceDataplane,
} from "src/clients/flow-api";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useIsTaktileAdmin } from "src/store/AuthStore";
import { logger } from "src/utils/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  workspace: WorkspaceDataplane | undefined;
  agentsModuleId: string | undefined;
  awsAccountName: string | undefined;
  orgName: string | undefined;
};

const HeaderMetadata: React.FC<{
  workspace: WorkspaceDataplane;
  orgName: string | undefined;
  awsAccountName: string | undefined;
}> = ({ workspace, orgName, awsAccountName }) => {
  const items: { label: string; value: string }[] = [
    { label: "Workspace", value: workspace.slug },
    { label: "Organization", value: orgName ?? "—" },
    { label: "AWS account", value: awsAccountName ?? "—" },
    { label: "Region", value: workspace.aws_region },
  ];
  return (
    <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
      {items.map((item) => (
        <div key={item.label}>
          <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
            {item.label}
          </div>
          <div className="font-inter-medium-13px text-gray-900">
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
};

const extractErrorDetail = (err: unknown): string => {
  if (axios.isAxiosError(err) && err.response?.data?.detail) {
    const detail = err.response.data.detail;
    return typeof detail === "string" ? detail : JSON.stringify(detail);
  }
  return err instanceof Error ? err.message : String(err);
};

type FormShape = {
  tier: FlowApiSchemasModelUsageTier;
  monthlyCostLimit: string;
  caseCopilotEnabled: boolean;
  customOverridesJson: string;
};

const TIER_OPTIONS = [
  {
    key: FlowApiSchemasModelUsageTier.DEFAULT,
    label: "Default — minimal quotas (trials, evaluations)",
  },
  {
    key: FlowApiSchemasModelUsageTier.PREMIUM,
    label: "Premium — standard production quotas",
  },
  {
    key: FlowApiSchemasModelUsageTier.CUSTOM,
    label: "Custom — adjusted per-model quotas (needs agents team)",
  },
];

const CUSTOM_DISABLED_TOOLTIP =
  "To switch to Custom, please reach out to Agents team.";

const NO_LIMIT_SENTINEL = -1;

const getDefaults = (workspace: WorkspaceDataplane | undefined): FormShape => {
  const config = (workspace?.config ?? {}) as WorkspaceConfig;
  const featureOverrides = (config.model_usage_feature_overrides ??
    {}) as Record<string, FlowApiSchemasModelUsageFeatureOverride>;
  const overrides = (config.model_usage_custom_overrides ?? {}) as Record<
    string,
    unknown
  >;
  return {
    tier: config.model_usage_tier ?? FlowApiSchemasModelUsageTier.DEFAULT,
    monthlyCostLimit: String(
      config.model_usage_monthly_cost_limit ?? NO_LIMIT_SENTINEL,
    ),
    caseCopilotEnabled: featureOverrides.case_copilot?.enabled ?? false,
    customOverridesJson: JSON.stringify(overrides, null, 2),
  };
};

const parseCostLimit = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < NO_LIMIT_SENTINEL) return null;
  return parsed;
};

const computeSimulationPayload = (
  tier: FlowApiSchemasModelUsageTier,
  overridesJson: string,
  caseCopilotEnabled: boolean,
): SimulateQuotasRequest | undefined => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(overridesJson || "{}");
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return undefined;
  }
  return {
    tier,
    custom_overrides: parsed as Record<string, unknown>,
    feature_overrides: { case_copilot: { enabled: caseCopilotEnabled } },
  };
};

const useSimulatedQuotasForForm = (
  control: Control<FormShape>,
  baseUrl: string | undefined,
) => {
  const tier = useWatch({ control, name: "tier" });
  const overrides = useWatch({ control, name: "customOverridesJson" });
  const caseCopilotEnabled = useWatch({ control, name: "caseCopilotEnabled" });

  const [debouncedOverrides] = useDebounce(overrides, 300);

  const payload = computeSimulationPayload(
    tier,
    debouncedOverrides,
    caseCopilotEnabled,
  );

  return useSimulatedQuotas(baseUrl, payload);
};

export const EditLlmQuotaModal: React.FC<Props> = ({
  isOpen,
  onClose,
  afterLeave,
  workspace,
  agentsModuleId,
  awsAccountName,
  orgName,
}) => {
  const isTaktileAdmin = useIsTaktileAdmin();
  const patchWorkspace = usePatchWorkspace();
  const createOperation = useCreateOperation();
  const savedTier =
    (workspace?.config as WorkspaceConfig | undefined)?.model_usage_tier ??
    FlowApiSchemasModelUsageTier.DEFAULT;
  const canPickCustom =
    isTaktileAdmin || savedTier === FlowApiSchemasModelUsageTier.CUSTOM;

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<FormShape>({
    values: getDefaults(workspace),
  });

  const watchedTier = useWatch({ control, name: "tier" });
  const watchedOverrides = useWatch({ control, name: "customOverridesJson" });

  const simulatedQuotas = useSimulatedQuotasForForm(
    control,
    workspace?.base_url,
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormat = () => {
    clearErrors("customOverridesJson");
    try {
      const parsed = JSON.parse(watchedOverrides);
      setValue("customOverridesJson", JSON.stringify(parsed, null, 2), {
        shouldDirty: true,
      });
    } catch {
      setError("customOverridesJson", {
        type: "manual",
        message: "Invalid JSON",
      });
    }
  };

  const onSubmit = async (data: FormShape) => {
    if (!workspace) return;

    const costLimit = parseCostLimit(data.monthlyCostLimit);
    if (costLimit === null) {
      setError("monthlyCostLimit", {
        type: "manual",
        message: "Must be an integer ≥ -1. Use -1 for no limit.",
      });
      return;
    }

    let parsedOverrides: object;
    try {
      const parsed = JSON.parse(data.customOverridesJson || "{}");
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        throw new Error("Overrides must be a JSON object");
      }
      parsedOverrides = parsed;
    } catch (err) {
      setError("customOverridesJson", {
        type: "manual",
        message:
          err instanceof Error && err.message ? err.message : "Invalid JSON",
      });
      return;
    }

    const modelUsagePatch = {
      model_usage_tier: data.tier,
      model_usage_monthly_cost_limit: costLimit,
      model_usage_custom_overrides: parsedOverrides,
      model_usage_feature_overrides: {
        case_copilot: { enabled: data.caseCopilotEnabled },
      },
    };

    try {
      await patchWorkspace.mutateAsync({
        ...workspace,
        config: {
          ...(workspace.config ?? {}),
          ...modelUsagePatch,
        } as WorkspaceConfig,
      });
    } catch (err) {
      const description = extractErrorDetail(err);
      logger.error(`Failed to patch workspace config: ${description}`);
      toastActions.failure({
        title: "Failed to update workspace config",
        description,
      });
      return;
    }

    if (agentsModuleId) {
      try {
        await createOperation.mutateAsync({
          data: {
            type: "module_operations",
            attributes: { patch: { config: modelUsagePatch } },
            relationships: {
              module: { data: { type: "modules", id: agentsModuleId } },
            },
          },
        });
      } catch (err) {
        const description = extractErrorDetail(err);
        logger.error(
          `Workspace config saved, but Agents module operation failed: ${description}`,
        );
        toastActions.failure({
          title: "Workspace config saved, but Agents module operation failed",
          description: `${description} — retry to enqueue the module operation.`,
        });
        return;
      }
    }

    toastActions.success({ title: "Model quotas updated" });
    handleClose();
  };

  const tierOptions = TIER_OPTIONS.map((opt) => {
    const isCustom = opt.key === FlowApiSchemasModelUsageTier.CUSTOM;
    return {
      key: opt.key,
      value: opt.label,
      disabled: isCustom && !canPickCustom,
      disabledTooltip:
        isCustom && !canPickCustom ? CUSTOM_DISABLED_TOOLTIP : undefined,
    };
  });

  return (
    <Modal
      afterLeave={afterLeave}
      open={isOpen}
      size="lg"
      onClose={handleClose}
    >
      <Modal.Header
        description={
          workspace ? (
            <HeaderMetadata
              awsAccountName={awsAccountName}
              orgName={orgName}
              workspace={workspace}
            />
          ) : undefined
        }
      >
        Configure LLM usage
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="space-y-6">
            <section>
              <div className="mb-3 font-inter-medium-13px text-gray-500 tracking-wide">
                Quota tier
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="mb-1.5 block font-inter-medium-13px text-gray-900">
                    Tier
                  </label>
                  <Controller
                    control={control}
                    name="tier"
                    render={({ field }) => (
                      <Select
                        dataLoc="llm-quota-tier-select"
                        options={tierOptions}
                        value={field.value}
                        fullWidth
                        onChange={(val) =>
                          field.onChange(val as FlowApiSchemasModelUsageTier)
                        }
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block font-inter-medium-13px text-gray-900">
                    Monthly cost limit
                  </label>
                  <div className="flex items-center gap-2">
                    <Controller
                      control={control}
                      name="monthlyCostLimit"
                      render={({ field }) => (
                        <Input
                          {...field}
                          data-loc="llm-quota-cost-limit"
                          errored={!!errors.monthlyCostLimit}
                          monospaced
                          prefixText="$"
                          type="number"
                          fullWidth
                        />
                      )}
                    />
                  </div>
                  {errors.monthlyCostLimit ? (
                    <ErrorHint>{errors.monthlyCostLimit.message}</ErrorHint>
                  ) : (
                    <p className="mt-1 font-inter-normal-12px text-gray-500">
                      Use -1 for no limit.
                    </p>
                  )}
                </div>
              </div>
              {watchedTier === FlowApiSchemasModelUsageTier.CUSTOM && (
                <div className="mt-2">
                  <Callout
                    dataLoc="llm-quota-custom-warning"
                    icon={faTriangleExclamation}
                    type="warning"
                  >
                    <strong>
                      Custom tier needs approval from the agents team.
                    </strong>{" "}
                    It lets you set different limits per model and may override
                    the monthly spend cap. Check with #team-agents before
                    saving.
                  </Callout>
                </div>
              )}
            </section>

            <section>
              <div className="mb-3 font-inter-medium-13px text-gray-500 tracking-wide">
                Features that require custom quotas
              </div>
              <Controller
                control={control}
                name="caseCopilotEnabled"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Switch
                      dataLoc="llm-quota-case-copilot"
                      enabled={field.value}
                      onChange={field.onChange}
                    />
                    <span className="font-inter-medium-13px text-gray-900">
                      Case Copilot
                    </span>
                  </div>
                )}
              />
            </section>

            {watchedTier === FlowApiSchemasModelUsageTier.CUSTOM && (
              <section>
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Custom overrides
                    </div>
                    <p className="mt-0.5 font-inter-normal-12px text-gray-500">
                      JSON · per-model overrides on top of premium
                    </p>
                  </div>
                  <Button
                    dataLoc="llm-quota-format-json"
                    size="sm"
                    variant="secondary"
                    onClick={handleFormat}
                  >
                    Format
                  </Button>
                </div>
                <div className="h-80">
                  <Controller
                    control={control}
                    name="customOverridesJson"
                    render={({ field }) => (
                      <CodeEditor
                        dataLoc="llm-quota-overrides-editor"
                        drawErrorBorder={!!errors.customOverridesJson}
                        language="json"
                        lineWrapping
                        value={field.value}
                        onChange={(val) => {
                          field.onChange(val);
                          clearErrors("customOverridesJson");
                        }}
                      />
                    )}
                  />
                </div>
                {errors.customOverridesJson && (
                  <ErrorHint>{errors.customOverridesJson.message}</ErrorHint>
                )}
              </section>
            )}

            <section>
              <div className="mb-3 font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                Effective quotas at this tier
              </div>
              <SimulatedQuotasTable
                data={simulatedQuotas.data}
                isError={simulatedQuotas.isError}
                isFetching={simulatedQuotas.isFetching}
                isLoading={simulatedQuotas.isLoading}
              />
            </section>
          </div>
        </Modal.Content>

        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="llm-quota-save"
              disabled={
                isSubmitting ||
                patchWorkspace.isPending ||
                createOperation.isPending
              }
              htmlType="submit"
              loading={
                isSubmitting ||
                patchWorkspace.isPending ||
                createOperation.isPending
              }
              variant="primary"
            >
              Save
            </Button>
          }
          secondaryButton="Cancel"
        />
      </form>
    </Modal>
  );
};
